import React, { useEffect, useRef, useState } from 'react';
import EmojiPicker, { EmojiClickData } from 'emoji-picker-react';
import { useParamsContext } from '../../../contexts/ParamsContext';
import { EstabelecimentosService } from './Service';
import { useAppContext } from '../../../contexts/AppContext';
import { useEstabsContext } from './Context';
import { useEstabsContext as useGlobalEstabsContext } from '../../../contexts/EstabsContext';
import { Smile } from 'lucide-react';

const service = new EstabelecimentosService();
const DEFAULT_CONFIRMATION_MESSAGE =
    '✅ Agendamento realizado com sucesso!\\nSessão finalizada.';

const parseBooleanFlag = (value: unknown) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true';
    return false;
};

export default function EstabelecimentosForm() {
    const { clear, id } = useParamsContext();
    const { data, reloadData } = useEstabsContext();
    const { reloadEstab } = useGlobalEstabsContext();
    const { successSnackbar, errorSnackbar } = useAppContext();

    const [disabledButton, setDisabledButton] = useState(false);

    // 📍 Informações básicas
    const [name, setName] = useState('');
    const [urlApp, setUrlApp] = useState('');
    const [enablePlanoAgendamentoDiscountField, setEnablePlanoAgendamentoDiscountField] =
        useState(false);
    const [blockAttendantsSeeOthersInSelect, setBlockAttendantsSeeOthersInSelect] =
        useState(false);
    const [billingExempt, setBillingExempt] = useState(false);

    // 💬 WhatsApp
    const [whatsappToken, setWhatsappToken] = useState('');
    const [whatsappPhoneNumberId, setWhatsappPhoneNumberId] = useState('');
    const [whatsappThankYouGender, setWhatsappThankYouGender] = useState<'masculine' | 'feminine'>(
        'masculine',
    );
    const [whatsappSchedulingConfirmationMessage, setWhatsappSchedulingConfirmationMessage] = useState(
        DEFAULT_CONFIRMATION_MESSAGE
    );

    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const emojiPickerRef = useRef<HTMLDivElement | null>(null);

    const handleEmojiClick = (emojiData: EmojiClickData) => {
        setWhatsappSchedulingConfirmationMessage((prev) => prev + emojiData.emoji);
    };

    useEffect(() => {
        const doc: any = data?.find((e) => e._id === id);

        if (doc) {
            setName(doc.name || '');
            setUrlApp(doc.urlApp || '');
            setWhatsappToken(doc.whatsappToken || '');
            setWhatsappPhoneNumberId(doc.whatsappPhoneNumberId || '');
            setWhatsappThankYouGender(
                doc.whatsappThankYouGender === 'feminine' ? 'feminine' : 'masculine',
            );
            setWhatsappSchedulingConfirmationMessage(
                doc.whatsappSchedulingConfirmationMessage || DEFAULT_CONFIRMATION_MESSAGE
            );
            setEnablePlanoAgendamentoDiscountField(
                parseBooleanFlag(doc.enablePlanoAgendamentoDiscountField),
            );
            setBlockAttendantsSeeOthersInSelect(
                doc.allowAttendantsSeeOthersInSelect === false,
            );
            setBillingExempt(parseBooleanFlag(doc.billingExempt));

        }
    }, [id, data]);

    useEffect(() => {
        if (!showEmojiPicker) return;

        const onPointerDown = (e: MouseEvent | TouchEvent) => {
            const target = e.target as Node;
            if (emojiPickerRef.current?.contains(target)) return;
            setShowEmojiPicker(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown, { passive: true });

        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
        };
    }, [showEmojiPicker]);

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setDisabledButton(true);

        try {
            const estabelecimento: any = {
                name,
                urlApp,
                whatsappToken,
                whatsappPhoneNumberId,
                whatsappSchedulingConfirmationMessage,
                enablePlanoAgendamentoDiscountField,
                allowAttendantsSeeOthersInSelect: !blockAttendantsSeeOthersInSelect,
                billingExempt,
            };

            if (id) {
                estabelecimento.whatsappThankYouGender = whatsappThankYouGender;
                await service.update(id, estabelecimento);
                successSnackbar('Estabelecimento atualizado com sucesso!');
            } else {
                await service.create(estabelecimento);
                successSnackbar('Estabelecimento criado com sucesso!');
            }

            reloadData();
            reloadEstab();
        } catch (e: any) {
            console.error(e);
            errorSnackbar(e.message || 'Erro ao salvar o estabelecimento.');
        } finally {
            setDisabledButton(false);
            if (!id) {
                clear();
            }
        }
    };

    return (
        <form onSubmit={submit} className="p-8 space-y-8">
            {/* 🏢 Informações básicas */}
            <div>
                <h3 className="font-bold text-lg mb-3">Informações do Estabelecimento</h3>
                <div className="grid md:grid-cols-2 gap-4">
                    <label className="form-control w-full">
                        <div className="label">
                            <span className="label-text font-semibold">Nome</span>
                        </div>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Nome do estabelecimento"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                        />
                    </label>

                    <label className="form-control w-full">
                        <div className="label">
                            <span className="label-text font-semibold">URL do App</span>
                        </div>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="https://app.salonflow.com.br/barbearia"
                            value={urlApp}
                            onChange={(e) => setUrlApp(e.target.value)}
                        />
                    </label>
                </div>

                <label className="label cursor-pointer justify-start gap-3 mt-4">
                    <input
                        type="checkbox"
                        className="toggle toggle-primary"
                        checked={enablePlanoAgendamentoDiscountField}
                        onChange={(e) =>
                            setEnablePlanoAgendamentoDiscountField(e.target.checked)
                        }
                    />
                    <span className="label-text">
                        Exibir campo de desconto (%) no formulário de plano deste estabelecimento
                    </span>
                </label>

                <label className="label cursor-pointer justify-start gap-3 mt-2">
                    <input
                        type="checkbox"
                        className="toggle toggle-primary"
                        checked={blockAttendantsSeeOthersInSelect}
                        onChange={(e) =>
                            setBlockAttendantsSeeOthersInSelect(e.target.checked)
                        }
                    />
                    <span className="label-text">
                        Impedir que atendentes vejam outros atendentes no select
                    </span>
                </label>

                <label className="label cursor-pointer justify-start gap-3 mt-2">
                    <input
                        type="checkbox"
                        className="toggle toggle-primary"
                        checked={billingExempt}
                        onChange={(e) => setBillingExempt(e.target.checked)}
                    />
                    <span className="label-text">
                        Isentar de plano/cobrança do SalonFlow (contrato externo com acesso total)
                    </span>
                </label>
            </div>

            <div className="divider" />

            {/* 💬 Configuração WhatsApp */}
            <div>
                <h3 className="font-bold text-lg mb-3">Configuracao WhatsApp</h3>
                <div className="grid md:grid-cols-2 gap-4">
                    <label className="form-control w-full">
                        <div className="label">
                            <span className="label-text font-semibold">Token WhatsApp</span>
                        </div>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Token do WhatsApp Business API"
                            value={whatsappToken}
                            onChange={(e) => setWhatsappToken(e.target.value)}
                        />
                    </label>

                    <label className="form-control w-full">
                        <div className="label">
                            <span className="label-text font-semibold">ID Phone Number WhatsApp</span>
                        </div>
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Ex: 123456789012345"
                            value={whatsappPhoneNumberId}
                            onChange={(e) => setWhatsappPhoneNumberId(e.target.value)}
                        />
                    </label>
                </div>

                {!!id && (
                    <label className="form-control w-full max-w-md mt-4">
                        <div className="label">
                            <span className="label-text font-semibold">Gênero dos agradecimentos automáticos</span>
                        </div>
                        <select
                            className="select select-bordered w-full"
                            value={whatsappThankYouGender}
                            onChange={(e) =>
                                setWhatsappThankYouGender(e.target.value as 'masculine' | 'feminine')
                            }
                        >
                            <option value="masculine">Masculino (Obrigado)</option>
                            <option value="feminine">Feminino (Obrigada)</option>
                        </select>
                        <span className="text-xs text-base-content/60 mt-1">
                            Define como o sistema agradece nas mensagens automáticas do WhatsApp.
                        </span>
                    </label>
                )}

                <div className="form-control mt-4">
                    <label className="label" htmlFor="whatsappSchedulingConfirmationMessage">
                        <span className="label-text font-semibold">
                            Mensagem de confirmacao do agendamento (WhatsApp/Instagram)
                        </span>
                    </label>
                    <div className="relative w-full max-w-2xl" ref={emojiPickerRef}>
                        <textarea
                            id="whatsappSchedulingConfirmationMessage"
                            className="textarea textarea-bordered w-full min-h-32 pr-10"
                            placeholder="✅ Agendamento realizado com sucesso!\nSessão finalizada."
                            value={whatsappSchedulingConfirmationMessage}
                            onChange={(e) => setWhatsappSchedulingConfirmationMessage(e.target.value)}
                        />
                        <button
                            type="button"
                            className="absolute top-2 right-2 btn btn-ghost btn-xs btn-circle"
                            onClick={() => setShowEmojiPicker((prev) => !prev)}
                            aria-label="Abrir emojis"
                        >
                            <Smile aria-hidden="true" />
                        </button>
                        {showEmojiPicker && (
                            <div className="absolute z-50 mt-2">
                                <EmojiPicker onEmojiClick={handleEmojiClick} lazyLoadEmojis skinTonesDisabled />
                            </div>
                        )}
                    </div>
                    <span className="text-xs text-gray-500 mt-1">
                        Variáveis: {'{{cliente}}'}, {'{{estabelecimento}}'}, {'{{servico}}'}, {'{{preco}}'},
                        {'{{atendente}}'}, {'{{data}}'}, {'{{hora}}'}, {'{{data_hora}}'}
                    </span>
                    <div className="text-xs text-gray-500 mt-1">
                        <div className="font-semibold">Exemplos</div>
                        <ul className="list-disc ml-5">
                            <li>{'{{cliente}}'} = Joao Silva</li>
                            <li>{'{{estabelecimento}}'} = Barbearia Central</li>
                            <li>{'{{servico}}'} = Corte</li>
                            <li>{'{{preco}}'} = R$ 50,00</li>
                            <li>{'{{atendente}}'} = Maria</li>
                            <li>{'{{data}}'} = 05/04/2024</li>
                            <li>{'{{hora}}'} = 14:30</li>
                            <li>{'{{data_hora}}'} = sexta, 05 de abril de 2024, às 14:30</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="divider" />

            <button
                className="btn btn-primary shadow-md w-full md:w-auto"
                disabled={disabledButton}
                type="submit"
            >
                {!!id ? 'Editar' : 'Adicionar'}
            </button>
        </form>
    );
}
